import qs from 'qs'
import { CHANGELOG_REVALIDATE_SECONDS, CMS_PAGE_CONCURRENCY } from '@/constants/cache'
import { cmsFetch } from '@/utils/cmsFetch'

const API_URL = process.env.NEXT_PUBLIC_SIGNOZ_CMS_API_URL
const API_PATH = process.env.SIGNOZ_CMS_CHANGELOG_PATH
const API_SUBSCRIPTION_PATH = process.env.NEXT_PUBLIC_SIGNOZ_CMS_SUBSCRIPTION_PATH

export const SupportedImageTypes = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
export const SupportedVideoTypes = ['.mp4', '.webm']

export enum DeploymentType {
  ALL = 'All',
  CLOUD_ONLY = 'Cloud Only',
  OSS_ONLY = 'OSS Only',
}

export const DeploymentTypeLabels = {
  [DeploymentType.ALL]: 'All',
  [DeploymentType.CLOUD_ONLY]: 'Cloud',
  [DeploymentType.OSS_ONLY]: 'Community Edition',
}

export const DeploymentTypeColors: Record<DeploymentType, string> = {
  [DeploymentType.ALL]: 'bg-signoz_robin-500',
  [DeploymentType.CLOUD_ONLY]: 'bg-signoz_sakura-500',
  [DeploymentType.OSS_ONLY]: 'bg-signoz_sienna-500',
}

export type Media = {
  id: number
  documentId: string
  ext: string
  url: string
  mime: string
  alternativeText: string
  [key: string]: any // Allow other fields (e.g., mime, size) to be flexible
}

export type Feature = {
  id: number
  documentId: string
  title: string
  sort_order: number | null
  createdAt: string
  updatedAt: string
  publishedAt: string
  description: string
  deployment_type: string | null
  media: Media | null
}

export type ReleaseChangelog = {
  id: number
  documentId: string
  version: string
  release_date: string
  bug_fixes: string
  maintenance: string | null
  createdAt: string
  updatedAt: string
  publishedAt: string
  features: Feature[]
}

type ChangelogApiResponse = {
  data: ReleaseChangelog[]
  meta: {
    pagination: TPagination
  }
}

export type ChangelogByIdApiResponse = {
  data: ReleaseChangelog
  meta: {}
}

export type TPagination = {
  page: number
  pageSize: number
  pageCount: number
  total: number
}

type ChangelogEntriesResponse = {
  changelogs: ReleaseChangelog[]
  pagination: TPagination
}

interface FetchChangelogEntriesParams {
  deployment_type?: DeploymentType
  page?: number
  pageSize?: number
  excludeDocumentIds?: string[]
}

export const fetchChangelogEntries = async (
  params: FetchChangelogEntriesParams
): Promise<ChangelogEntriesResponse> => {
  try {
    const queryObject = {
      sort: {
        release_date: 'desc',
      },
      populate: {
        features: {
          sort: ['sort_order:asc'],
          populate: {
            media: {
              fields: 'id,ext,url,mime,alternativeText', // Specify the fields you want to include
            },
          },
        },
      },
      pagination: {
        page: params.page || 1, // Default to page 1 if not provided
        pageSize: params.pageSize || 2, // Default to page size of 10 if not provided
      },
    }

    if (params.excludeDocumentIds?.length) {
      queryObject['filters'] = {
        ...queryObject['filters'],
        documentId: {
          $notIn: params.excludeDocumentIds,
        },
      }
    }

    // If a specific deployment type is provided, filter out the others, All will not be excluded
    if (
      params?.deployment_type &&
      Object.values(DeploymentType).includes(params?.deployment_type) &&
      params.deployment_type !== DeploymentType.ALL
    ) {
      const excludedDeploymentTypes = Object.values(DeploymentType).filter(
        (type) => type !== params?.deployment_type && type !== DeploymentType.ALL
      )
      queryObject.populate.features['filters'] = {
        ...queryObject.populate.features['filters'],
        deployment_type: {
          $notIn: excludedDeploymentTypes, // Exclude the specified deployment type
        },
      }
    }

    const queryParams = qs.stringify(queryObject, {
      encode: false, // Prevent encoding of square brackets
      addQueryPrefix: true, // Add '?' at the beginning
      arrayFormat: 'repeat', // Use repeat format for arrays
    })

    const response = await cmsFetch(`${API_PATH}${queryParams}`, {
      next: {
        revalidate: CHANGELOG_REVALIDATE_SECONDS,
        tags: ['release-changelogs'],
      },
    })

    if (!response.ok) {
      const errorMessage = await response.text()
      throw new Error(`Network response was not ok: ${errorMessage}`)
    }

    const data: ChangelogApiResponse = await response.json()
    return {
      changelogs: data.data,
      pagination: data.meta.pagination,
    }
  } catch (error) {
    console.error('Error fetching changelog entries:', error)
    throw error
  }
}

export const saveChangelogSubscription = async (email: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}${API_SUBSCRIPTION_PATH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          email,
        },
      }),
    })
    if (!response.ok) {
      const errorMessage = await response.text()
      console.error('Network response was not ok:', errorMessage)
      return false
    }
    return true
  } catch (error) {
    console.error('Error saving changelog subscription:', error)
    return false
  }
}

export const fetchChangelogById = async (
  changelogId: string
): Promise<ChangelogByIdApiResponse> => {
  try {
    const queryObject = {
      populate: {
        features: {
          sort: ['sort_order:asc'],
          populate: {
            media: {
              fields: 'id,ext,url,mime,alternativeText', // Specify the fields you want to include
            },
          },
        },
      },
    }
    const queryParams = qs.stringify(queryObject, {
      encode: false, // Prevent encoding of square brackets
      addQueryPrefix: true, // Add '?' at the beginning
      arrayFormat: 'repeat', // Use repeat format for arrays
    })

    const response = await cmsFetch(`${API_PATH}/${changelogId}${queryParams}`, {
      next: {
        revalidate: CHANGELOG_REVALIDATE_SECONDS,
        tags: ['release-changelogs'],
      },
    })

    if (!response.ok) {
      const errorMessage = await response.text()
      throw new Error(`Network response was not ok: ${errorMessage}`)
    }
    const data: ChangelogByIdApiResponse = await response.json()
    return data
  } catch (error) {
    console.error(`Error fetching changelog by id ${changelogId}:`, error)
    throw error
  }
}

// MDX Content schema — see utils/dateUtils.ts for date field semantics
export type MDXContent = {
  id: number
  documentId: string
  title: string
  slug: string
  path: string
  content: string
  excerpt?: string
  publishedAt: string
  createdAt: string
  updatedAt: string
  published_date?: string
  updated_date?: string
  image?: string
  layout?: string
  [key: string]: any
}

export type MDXContentApiResponse = {
  data: MDXContent[]
  meta: {
    pagination: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
  }
}

export type MDXContentByIdApiResponse = {
  data: MDXContent
  meta: {}
}

// Cache for storing paths to avoid repeated API calls - avoid later maybe
let pathsCache: string[] | null = null
let pathsCacheTimestamp: number = 0
const PATHS_CACHE_TTL = 5 * 60 * 1000 // 5 minutes

// For related articles component, populate: '*' does shallow populate, so nested relations in related_articles component are not populated.
// Specific fields that do not exist on a content type make the API throw.
// Example: keywords do not exist on faqs, so each collection gets its own populate map.
const relatedArticlesPopulate = {
  populate: '*',
}

const commonContentPopulate = {
  authors: '*',
  related_articles: relatedArticlesPopulate,
}

const singleContentPopulateByCollection: Record<string, Record<string, unknown>> = {
  faqs: {
    ...commonContentPopulate,
    tags: '*',
  },
  'case-studies': {
    ...commonContentPopulate,
    tags: '*',
  },
  comparisons: {
    ...commonContentPopulate,
    tags: '*',
    keywords: '*',
  },
  guides: {
    ...commonContentPopulate,
    tags: '*',
    keywords: '*',
  },
  opentelemetries: {
    ...commonContentPopulate,
    tags: '*',
    keywords: '*',
  },
  blogs: {
    ...commonContentPopulate,
    tags: '*',
    keywords: '*',
  },
  docs: {
    ...commonContentPopulate,
    tags: '*',
    keywords: '*',
  },
}

type SettledResult<T> = { status: 'fulfilled'; value: T } | { status: 'rejected'; reason: unknown }

async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  limit: number
): Promise<SettledResult<T>[]> {
  const results: SettledResult<T>[] = new Array(tasks.length)
  let next = 0

  async function worker() {
    while (next < tasks.length) {
      const index = next++
      try {
        results[index] = { status: 'fulfilled', value: await tasks[index]() }
      } catch (reason) {
        results[index] = { status: 'rejected', reason }
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker))
  return results
}

const pageFetches = new Map<string, Promise<SettledResult<MDXContentApiResponse>[]>>()

// Fetch MDX content by path or all content for a collection
export const fetchMDXContentByPath = async (
  collectionName: string,
  path?: string,
  deployment_status?: string,
  fetchAll: boolean = false,
  fields?: string[]
): Promise<MDXContentByIdApiResponse | MDXContentApiResponse> => {
  try {
    const queryObject: any = {
      populate: fetchAll ? '*' : singleContentPopulateByCollection[collectionName] || '*',
      pagination: {
        page: 1,
        pageSize: 100,
      },
      sort: ['publishedAt:desc'],
    }

    if (fields && fields.length > 0) {
      queryObject.fields = fields
    }

    // Add filters only if not fetching all
    if (!fetchAll) {
      if (!path) {
        throw new Error('Path is required when fetchAll is false')
      }
      // Ensure path starts with a slash and has no trailing slashes
      const normalizedPath = `/${path.replace(/^\/+|\/+$/g, '')}`

      queryObject.filters = {
        path: {
          $eq: normalizedPath,
        },
      }

      if (deployment_status) {
        queryObject.filters.deployment_status = {
          $eq: deployment_status,
        }
      }
    } else {
      // Optionally filter by deployment status for list views
      if (deployment_status) {
        queryObject.filters = {
          deployment_status: {
            $eq: deployment_status,
          },
        }
      }
    }

    if (!API_URL) {
      throw new Error('NEXT_PUBLIC_SIGNOZ_CMS_API_URL is not configured')
    }

    // If fetchAll is true, fetch all pages and combine results
    if (fetchAll) {
      const BATCH_PAGE_SIZE = 50
      let allData: MDXContent[] = []

      const initialQueryObject = {
        ...queryObject,
        pagination: { page: 1, pageSize: BATCH_PAGE_SIZE },
      }

      const initialQueryParams = qs.stringify(initialQueryObject, {
        encode: false,
        addQueryPrefix: true,
        arrayFormat: 'repeat',
      })

      const initialResponse = await cmsFetch(`/api/${collectionName}${initialQueryParams}`, {
        cache: 'force-cache',
        next: {
          tags: [`${collectionName}-list`, 'mdx-content-list'],
        },
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!initialResponse.ok) {
        if (initialResponse.status === 404) {
          throw new Error('Content not found')
        }
        const errorMessage = await initialResponse.text()
        throw new Error(`Network response was not ok: ${initialResponse.status} ${errorMessage}`)
      }

      const initialData: MDXContentApiResponse = await initialResponse.json()

      if (initialData.data && initialData.data.length > 0) {
        allData = initialData.data
      }

      const totalPages = initialData.meta.pagination.pageCount

      if (totalPages > 1) {
        const pageFetchKey = [
          collectionName,
          deployment_status ?? '',
          fields?.join(',') ?? '',
          totalPages,
        ].join('|')

        let pageFetch = pageFetches.get(pageFetchKey)
        if (!pageFetch) {
          const pageTasks: Array<() => Promise<MDXContentApiResponse>> = []
          for (let i = 2; i <= totalPages; i++) {
            const pageQueryObject = {
              ...queryObject,
              pagination: { page: i, pageSize: BATCH_PAGE_SIZE },
            }

            const pageQueryParams = qs.stringify(pageQueryObject, {
              encode: false,
              addQueryPrefix: true,
              arrayFormat: 'repeat',
            })

            pageTasks.push(() =>
              cmsFetch(`/api/${collectionName}${pageQueryParams}`, {
                cache: 'force-cache',
                next: {
                  tags: [`${collectionName}-list`, 'mdx-content-list'],
                },
                headers: {
                  'Content-Type': 'application/json',
                },
              }).then(async (res) => {
                if (!res.ok) {
                  const msg = await res.text()
                  throw new Error(`Failed to fetch page ${i}: ${res.status} ${msg}`)
                }
                return res.json() as Promise<MDXContentApiResponse>
              })
            )
          }

          pageFetch = runWithConcurrency(pageTasks, CMS_PAGE_CONCURRENCY).finally(() => {
            pageFetches.delete(pageFetchKey)
          })
          pageFetches.set(pageFetchKey, pageFetch)
        }

        const pagesResults = await pageFetch
        for (const result of pagesResults) {
          if (result.status === 'fulfilled') {
            if (result.value.data?.length > 0) {
              allData = allData.concat(result.value.data)
            }
          } else {
            console.error(`Failed to fetch a page of ${collectionName}:`, result.reason)
          }
        }
      }

      const finalMeta = initialData.meta
      finalMeta.pagination = {
        page: 1,
        pageSize: allData.length,
        pageCount: 1,
        total: allData.length,
      }

      return {
        data: allData,
        meta: finalMeta,
      }
    }

    // Single content fetch
    const queryParams = qs.stringify(queryObject, {
      encode: false,
      addQueryPrefix: true,
      arrayFormat: 'repeat',
    })
    const response = await cmsFetch(`/api/${collectionName}${queryParams}`, {
      cache: 'force-cache',
      next: {
        tags: [`${collectionName}-${path}`, `mdx-content-${path}`],
      },
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Content not found')
      }
      const errorMessage = await response.text()
      throw new Error(`Network response was not ok: ${response.status} ${errorMessage}`)
    }

    const data: MDXContentApiResponse = await response.json()

    if (!data.data || data.data.length === 0) {
      throw new Error('Content not found')
    }

    return {
      data: data.data[0],
      meta: {},
    }
  } catch (error) {
    console.error(`Error fetching MDX content for ${collectionName}:`, error)
    throw error
  }
}

// Clear the paths cache (useful for revalidation)
export const clearPathsCache = (): void => {
  pathsCache = null
  pathsCacheTimestamp = 0
}
